import {
    IDatasetTypeMetadata,
    IDynamicDatasetTypeMetadata
} from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { GlobalVariables } from 'app/services/globals';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { format } from 'app/shared/utils/string.util';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ReadMode } from 'app/model/dataflow/dataset-settings/sap-odp-settings';

const copySourceControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'select',
            bind: 'extractionMode',
            label: stringResources.Extraction_Mode,
            options: [
                {
                    label: stringResources.Usql_CompilationModeOptions_Full,
                    value: 'Full'
                },
                {
                    label: stringResources.ModelName_DelaFormatDataset,
                    value: 'Delta'
                },
                {
                    label: stringResources.Recovery,
                    value: 'Recovery'
                }
            ]
        },
        dynamicContentTextClass: 'input-spaced'
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'deltaToken',
            label: stringResources.DeltaToken
        },
        dynamicContentTextClass: 'input-spaced'
    },
    {
        type: 'sap-projection-selection',
        parameterizable: true,
        showSelection: false,
        bind: {
            projection: 'projection'
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'array-container',
            bind: 'selection',
            label: stringResources.Selection,
            child: {
                type: 'array-item',
                children: [
                    {
                        type: 'textbox',
                        bind: 'itemPropertyNamePlaceholder'
                    }
                ]
            },
            expandable: false
        },
        dynamicContentTextClass: 'input-spaced'
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox-number',
            bind: 'batchSize',
            label: stringResources.ModelName_BatchSize
        },
        dynamicContentTextClass: 'input-spaced'
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox-number',
            bind: 'httpRequestTimeout',
            label: stringResources.Timeout
        },
        dynamicContentTextClass: 'input-spaced'
    }
];

export const sapODataDatasetMetadata: IDynamicDatasetTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(
        injector,
        'ExposureControlService'
    );
    const metadata: IDatasetTypeMetadata = {
        type: adfModelTypeMap.sapOData.datasetType,
        displayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.SapOData_DisplayText),
        supportLinkedServiceTypes: [adfModelTypeMap.sapOData.linkedServiceType],
        splitTablePath: true,
        separatedSchemaTableSelection: true,
        isSource: GlobalVariables.enableCopySapOData,
        isSink: false,
        datasetKind: 'table',
        icon: {
            className: 'svg-i-100x100-Sap'
        },
        isFirstClass: true,
        notSupportLookupActivity: true,
        notSupportCopySourceQuery: true,
        schemaAlias: 'servicePath',
        supportDataFlowSource: () => exposureControlService.enableSAPOData,
        tableAlias: 'entityName',
        schemaNameDisplay: stringResources.ServicePath_Label,
        tableNameDisplay: stringResources.TableNameDisplay_EntityName,
        tableNamePlural: stringResources.Entities,
        category: ['Database'],
        supportCreatedAsV2: true,
        notSupportPhysicalType: true,
        copySourceControls,
        dataflowSourceControls: [
            {
                type: 'parameterizable',
                child: {
                    type: 'select',
                    bind: 'sapReadMode',
                    label: stringResources.DataFlowRunMode,
                    options: [
                        {
                            label: stringResources.DataFlowFullOnFirstRunThenIncremental,
                            value: ReadMode.FULL_AND_INCREMENTAL_LOAD
                        },
                        { label: stringResources.DataFlowFullOnEveryRun, value: ReadMode.FULL_LOAD },
                        {
                            label: stringResources.DataFlowIncrementalChangesOnly,
                            value: ReadMode.INCREMENTAL_LOAD,
                            disabled: true,
                            tooltip: stringResources.DataFlowIncrementalChangesOnlyDisabledTooltip
                        }
                    ],
                    isDataFlowExpression: true
                },
                showNavigationMethod: 'hover',
                dynamicContentTextClass: 'input-spaced',
                showErrorIcon: false,
                isDataFlowExpression: true
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    bind: 'batchSize',
                    label: stringResources.ModelName_BatchSize,
                    isDataFlowExpression: true
                },
                dynamicContentTextClass: 'input-spaced',
                showErrorIcon: false,
                isDataFlowExpression: true
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    bind: 'timeout',
                    label: stringResources.Timeout,
                    isDataFlowExpression: true
                },
                dynamicContentTextClass: 'input-spaced',
                showErrorIcon: false,
                isDataFlowExpression: true
            },
            {
                type: 'sap-odp-key-columns',
                bind: 'sapOdpKeyColumns',
                required: true,
                hide: `{ sapReadMode.expression === 'fullLoad' }`
            }
        ]
    };

    return metadata;
};
